import defaults from 'lodash/defaults'

import popoverTemplate from './vd-suggestion-popover-error.html'

const DEFAULT_OPTIONS = {
  repositionOnPopoverResize: true,
  inheritWidthFromTrigger: 'width',
  hasPopoverList: true,
}

/**
 * Service for opening and closing a popover to display a list of suggestions in (for Dropdown and Autocomplete
 * components).
 *
 * @class SuggestionPopoverService
 */
export default class SuggestionPopoverService {
  constructor($q, vdPopover) {
    'ngInject'
    this._$q = $q
    this._vdPopover = vdPopover
    this._isOpen = false
  }

  /**
   * Whether or not the suggestion list popover (or error popover) is currently open.
   *
   * @readonly
   * @property isOpen
   * @type {Boolean}
   */
  get isOpen() {
    return this._isOpen || this._isErrorOpen
  }

  /**
   * Open the suggestions error popover that displays an error message in the event where searching for suggestions
   * failed.
   *
   * @method openError
   *
   * @param  {Object} options The options to configure the popover, see vd-popover-service for more details
   *
   * @return {Promise} A promise resolved once the error popover is closed
   */
  openError(options) {
    const popoverErrorOptions = defaults(
      {
        template: popoverTemplate,
      },
      options,
      DEFAULT_OPTIONS
    )

    this._isErrorOpen = true

    return this._vdPopover
      .open(popoverErrorOptions)
      .then(popover => popover.closed)
      .finally(() => {
        this._isErrorOpen = false
      })
  }

  /**
   * Open the suggestion list popover.
   *
   * @method open
   *
   * @param  {Object} options The options to configure the popover, see vd-popover-service for more details
   *
   * @return {Promise<Suggestion|undefined>} A promise that is resolved with the selected suggestion when the popover is
   *         closed (or undefined if no suggestion was selected).
   */
  open(options) {
    const popoverOptions = defaults({}, options, DEFAULT_OPTIONS)
    this._isOpen = true
    return this._vdPopover
      .open(popoverOptions)
      .then(popover => popover.closed)
      .finally(() => {
        this._isOpen = false
      })
  }

  /**
   * Close the suggestion list popover if its open.
   *
   * @method close
   *
   * @return {Promise} A promise resolved once the popover is closed
   */
  close() {
    if (this.isOpen) {
      return this._vdPopover.close({ match: '.vd-suggestions-popover' })
    } else {
      return this._$q.resolve()
    }
  }
}
